import Grid from "@mui/material/Grid";
import { useEffect, useState, useMemo, useCallback } from "react";

// @mui material components

// Argon Dashboard 2 PRO MUI components
import { useTranslation } from "react-i18next";

import CRMFunnelBoard from "0.Mine/0.Components/CRM/Leads/CRMFunnelBoard";
import { mapRawLeadToLead, UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { LeadState } from "0.Mine/3.Interfaces/lead";
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 PRO MUI example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";
import CRMTable from "examples/Tables/Table/CRMLeadsTable";

const columns = [
  { name: "name", align: "center" },
  { name: "company", align: "left" },
  { name: "email", align: "left" },
  { name: "state", align: "center" },
  { name: "value", align: "center" },
  { name: "phone", align: "center" },
  { name: "notes", align: "center" },
  { name: "actions", align: "right" },
  { name: "created_at", align: "center" },
];

function CRMReport() {
  return (
    <DashboardLayout>
      <CRMReportWithContext />
    </DashboardLayout>
  );
}

function CRMReportWithContext() {
  const { getAllLeads, addLead, editLead, deleteLead, searchLeadByTerm } = UseHandleLeads();
  const { session } = useBusinessContext();
  const [leads, setLeads] = useState([]);
  const { t } = useTranslation();
  const [biz, setbiz] = useState(null);
  const [searchFields, setSearchFields] = useState({});
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 0,
    pageSize: 10,
    lastPage: 1,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (session?.selectedBusiness?.id) {
        setbiz(session.selectedBusiness);
        try {
          const response = await getAllLeads(
            session.selectedBusiness.id,
            pagination.currentPage + 1,
            pagination.pageSize
          );

          setLeads(response.data || []);
          setPagination((prev) => ({
            ...prev,
            total: response.total,
            lastPage: response.lastPage || Math.ceil(response.total / pagination.pageSize),
          }));
        } catch (error) {
          setLeads([]);
        }
      } else {
        setLeads([]);
      }
    };

    fetchData();
  }, [session?.selectedBusiness?.id, pagination.currentPage, pagination.pageSize]);

  const handlePageChange = useCallback(
    async (newPage) => {
      try {
        if (session?.selectedBusiness?.id) {
          const hasSearchTerms = Object.values(searchFields).some((term) => term.length > 0);
          const response = await (hasSearchTerms
            ? searchLeadByTerm(
                session.selectedBusiness.id,
                searchFields,
                newPage,
                pagination.pageSize
              )
            : getAllLeads(session.selectedBusiness.id, newPage, pagination.pageSize));

          setLeads(response.data || []);
          setPagination((prev) => ({
            ...prev,
            total: response.total,
            lastPage: response.lastPage || Math.ceil(response.total / pagination.pageSize),
          }));
        }
      } catch (error) {
        setLeads([]);
      }
    },
    [session?.selectedBusiness?.id, pagination.pageSize, searchFields]
  );

  const handleSearch = useCallback(
    async (filters) => {
      if (!session?.selectedBusiness?.id) return;

      setSearchFields(filters);

      try {
        const hasSearchTerms = Object.values(filters).some((term) => term.length > 0);
        const response = hasSearchTerms
          ? await searchLeadByTerm(session.selectedBusiness.id, filters, 1, pagination.pageSize)
          : await getAllLeads(session.selectedBusiness.id, 1, pagination.pageSize);

        setLeads(response.data || []);
        setPagination((prev) => ({
          ...prev,
          currentPage: 0,
          total: response.total,
          lastPage: response.lastPage || Math.ceil(response.total / pagination.pageSize),
        }));
      } catch (error) {
        setLeads([]);
      }
    },
    [session?.selectedBusiness?.id, pagination.pageSize]
  );

  const handleLeadUpdate = useCallback(
    async (leadId, updates = {}) => {
      try {
        // Find the lead to update
        const leadToUpdate = leads.find((lead) => lead.id === leadId);
        if (!leadToUpdate) return;

        // Create updated lead object with any combination of fields
        const updatedLead = {
          ...leadToUpdate,
          ...updates,
        };

        // Update the lead in the database
        const response = await editLead(biz.id, updatedLead);

        // Update local state
        setLeads((prevLeads) =>
          prevLeads.map((lead) => (lead.id === leadId ? { ...lead, ...updates } : lead))
        );
      } catch (error) {
        console.error("Error updating lead:", error);
      }
    },
    [leads, biz, editLead]
  );

  const handleStatusChange = useCallback(
    (leadId, newStatus) => {
      handleLeadUpdate(leadId, { state: newStatus });
    },
    [handleLeadUpdate]
  );

  const handleValueChange = useCallback(
    (leadId, newValue) => {
      handleLeadUpdate(leadId, { value: newValue });
    },
    [handleLeadUpdate]
  );

  const handleDeleteLead = useCallback(
    async (leadId) => {
      try {
        // Delete the lead from the database
        if (biz?.id) {
          // Update local state by removing the deleted lead
          setLeads((prevLeads) => prevLeads.filter((lead) => lead.id !== leadId));

          await deleteLead(biz.id, leadId);
        }
      } catch (error) {
        console.error("Error deleting lead:", error);
      }
    },
    [biz]
  );

  const leadStats = useMemo(() => {
    const stats = leads.reduce(
      (acc, lead) => {
        if (lead.state) {
          acc.counts[lead.state] = (acc.counts[lead.state] || 0) + 1;
          acc.values[lead.state] = (acc.values[lead.state] || 0) + (parseFloat(lead.value) || 0);
        }
        return acc;
      },
      { counts: {}, values: {} }
    );
    return {
      newLeads: stats.counts[LeadState.New] || 0,
      lostLeads: stats.counts[LeadState.Lost] || 0, // Make sure this matches your enum
      notInterested: stats.counts[LeadState.NotInterested] || 0, // Make sure this matches your enum
      negotiationLead: stats.counts[LeadState.Negotiation] || 0,
      appointmentLead: stats.counts[LeadState.Appuntamento] || 0,
      wonLeads: stats.counts[LeadState.Won] || 0,
      won: stats.counts[LeadState.Won] || 0,
      totalValue: leads.reduce((acc, lead) => acc + (parseFloat(lead.value) || 0), 0),
      totalLeads: leads.length,
      totalWon: stats.values[LeadState.Won] || 0,
    };
  }, [leads]);

  const handleCreateLead = useCallback(
    async (newLead) => {
      setLeads((prevLeads) => [newLead, ...(prevLeads || [])]);
      try {
        const lead = mapRawLeadToLead(session?.selectedBusiness?.id, newLead);
        const leadId = await addLead(session?.selectedBusiness?.id, lead);
        //update the lead's id
        setLeads((prevLeads) =>
          prevLeads.map((lead) => (lead.id === newLead.id ? { ...lead, id: leadId } : lead))
        );
      } catch (error) {
        console.error("Error adding lead:", error);
      }
    },
    [session, addLead]
  );

  // Update the leadsTable memo to include pagination props
  const leadsTable = useMemo(
    () => (
      <CRMTable
        columns={columns}
        rows={leads.map((lead) => ({
          ...lead,
          created_at: lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "",
        }))}
        onStatusChange={handleStatusChange}
        onCreateLead={handleCreateLead}
        onValueChange={handleValueChange}
        onDeleteLead={handleDeleteLead}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        totalCount={pagination.total}
      />
    ),
    [
      leads,
      handleStatusChange,
      handleCreateLead,
      handleValueChange,
      handleDeleteLead,
      handlePageChange,
      handleSearch,
      pagination.total,
    ]
  );

  const memoizedFunnelBoard = useMemo(() => {
    return (
      <CRMFunnelBoard
        newLeads={leadStats.newLeads}
        lost={leadStats.lostLeads}
        appointment={leadStats.appointmentLead}
        negotiation={leadStats.negotiationLead}
        nonInterested={leadStats.notInterested}
        contract={leadStats.wonLeads}
      />
    );
  }, [
    leadStats.newLeads,
    leadStats.lostLeads,
    leadStats.appointmentLead,
    leadStats.negotiationLead,
    leadStats.notInterested,
    leadStats.wonLeads,
  ]);

  return (
    <ArgonBox py={3}>
      <BusinessNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ComplexStatisticsCard
                    image="https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/reports1.jpg"
                    icon="account_circle"
                    count={{ number: leadStats.newLeads, label: t("leads.newLeads") }}
                    percentage="+55%"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ComplexStatisticsCard
                    image="https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/reports2.jpg"
                    icon="touch_app"
                    count={{ number: leadStats.totalLeads, label: t("leads.totalLeads") }}
                    percentage="+124%"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ComplexStatisticsCard
                    image="https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/reports3.jpg"
                    icon="shopping_cart"
                    count={{ number: leadStats.totalValue, label: t("leads.potentialLeadValue") }}
                    percentage="+55%"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ComplexStatisticsCard
                    image="https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/reports4.jpg"
                    icon="thumb_up"
                    count={{ number: leadStats.totalWon, label: t("leads.newLeadValue") }}
                    percentage="+90%"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              {memoizedFunnelBoard}
            </Grid>
          </Grid>
        </ArgonBox>
        {leadsTable}
      </ArgonBox>
      <Footer />
    </ArgonBox>
  );
}

export default CRMReport;
